import { cn } from "@/utils/cn";
import type { GrepFormState, SearchContext } from "../types";
import { Examples } from "./Examples";
import { AdvancedField, AdvancedSection, FlagCheckbox } from "./AdvancedSection";
import { choice, FIELD, FIELD_MONO, HELP, LABEL } from "./styles";

const EXAMPLES = ["AlphaFold2", "NCT04280705", "IC50.{0,20}nM"];

/** How many advanced grep flags differ from their defaults. */
const countActiveAdvanced = (form: GrepFormState): number =>
  [
    form.ignoreCase,
    form.wholeWords,
    form.fixedStrings,
    form.countOnly,
    form.boolMode,
    form.maxMatches.trim() !== "",
    form.context.trim() !== "",
    form.section.trim() !== "",
    form.blockType.trim() !== "",
  ].filter(Boolean).length;

export const GrepControls = ({
  form,
  context,
  onChange,
  onSubmit,
}: {
  form: GrepFormState;
  /** Last search's saved result set, if any — enables the `--from` scope. */
  context: SearchContext | null;
  onChange: (next: GrepFormState) => void;
  onSubmit: () => void;
}) => (
  <div className="space-y-5">
    <div>
      <label
        htmlFor="playground-grep-pattern"
        className={LABEL}
      >
        Pattern (regex)
      </label>
      <input
        id="playground-grep-pattern"
        value={form.pattern}
        className={`${FIELD_MONO} mt-1`}
        onChange={(e) => onChange({ ...form, pattern: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
        }}
      />
      <p className={HELP}>
        Exact full-text matching over paper bodies — finds mentions that
        semantic search and abstract filters miss.
      </p>
    </div>

    <Examples
      mono
      items={EXAMPLES.map((value) => ({ value }))}
      onPick={(pattern) => onChange({ ...form, pattern })}
    />

    <fieldset>
      <legend className={LABEL}>
        Scope
      </legend>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-4">
        {(
          [
            { id: "corpus", label: "Whole corpus", disabled: false },
            { id: "paper", label: "Single paper", disabled: false },
            { id: "results", label: "Last result set", disabled: !context },
          ] as const
        ).map((scope) => (
          <button
            key={scope.id}
            type="button"
            aria-pressed={form.scope === scope.id}
            disabled={scope.disabled}
            title={
              scope.id === "results" && !context
                ? "Run a search first — grep can then filter that result set"
                : undefined
            }
            onClick={() => onChange({ ...form, scope: scope.id })}
            className={cn(
              choice(form.scope === scope.id),
              scope.disabled && "cursor-not-allowed opacity-40",
            )}
          >
            {scope.label}
          </button>
        ))}
      </div>
    </fieldset>

    {form.scope === "corpus" && (
      <div>
        <label
          htmlFor="playground-grep-source"
          className={LABEL}
        >
          Source
        </label>
        <select
          id="playground-grep-source"
          className={`${FIELD} mt-1`}
          value={form.source}
          onChange={(e) => onChange({ ...form, source: e.target.value })}
        >
          <option value="">Whole corpus (all papers)</option>
          <option value="pmc">PubMed Central</option>
          <option value="biorxiv">bioRxiv</option>
          <option value="medrxiv">medRxiv</option>
          <option value="arxiv">arXiv</option>
        </select>
      </div>
    )}

    {form.scope === "results" && context && (
      <p className={HELP}>
        Filters the {context.papers.length} docs saved as{" "}
        <span className="font-mono">{context.resultsId}</span> (“
        {context.query}”) — matching papers light up in the result-set panel.
      </p>
    )}

    {form.scope === "paper" && (
      <div>
        <label
          htmlFor="playground-grep-paper"
          className={LABEL}
        >
          Paper ID
        </label>
        <input
          id="playground-grep-paper"
          value={form.paperId}
          className={`${FIELD_MONO} mt-1`}
          onChange={(e) => onChange({ ...form, paperId: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
        />
      </div>
    )}

    <AdvancedSection
      title="Advanced options"
      activeCount={countActiveAdvanced(form)}
    >
      <div className="space-y-1.5">
        <FlagCheckbox
          label="Ignore case"
          flag="-i"
          checked={form.ignoreCase}
          disabled={form.scope === "results"}
          disabledReason="Result-set grep is always case-insensitive"
          onChange={(v) => onChange({ ...form, ignoreCase: v })}
        />
        <FlagCheckbox
          label="Whole words only"
          flag="-w"
          checked={form.wholeWords}
          onChange={(v) => onChange({ ...form, wholeWords: v })}
        />
        <FlagCheckbox
          label="Literal match (no regex)"
          flag="-F"
          checked={form.fixedStrings}
          onChange={(v) => onChange({ ...form, fixedStrings: v })}
        />
        <FlagCheckbox
          label="Count matches only"
          flag="-c"
          checked={form.countOnly}
          disabled={form.scope === "results"}
          disabledReason="Not available when grepping a result set"
          onChange={(v) => onChange({ ...form, countOnly: v })}
        />
        <FlagCheckbox
          label="Whole-document Boolean regex"
          flag="--bool"
          checked={form.boolMode}
          onChange={(v) => onChange({ ...form, boolMode: v })}
        />
        {form.boolMode && (
          <p className={HELP}>
            Each operand must be double-quoted: "CRISPR" AND ("base editing"
            OR "prime editing")
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        <AdvancedField label="Max matches" flag="-m" htmlFor="pg-grep-max">
          <input
            id="pg-grep-max"
            className={`${FIELD} mt-1`}
            value={form.maxMatches}
            placeholder="e.g. 50"
            inputMode="numeric"
            onChange={(e) => onChange({ ...form, maxMatches: e.target.value })}
          />
        </AdvancedField>
        <AdvancedField label="Context lines" flag="-C" htmlFor="pg-grep-ctx">
          <input
            id="pg-grep-ctx"
            className={`${FIELD} mt-1`}
            value={form.context}
            placeholder="e.g. 2"
            inputMode="numeric"
            disabled={form.scope === "results"}
            title={
              form.scope === "results"
                ? "Not available when grepping a result set"
                : undefined
            }
            onChange={(e) => onChange({ ...form, context: e.target.value })}
          />
        </AdvancedField>
        <AdvancedField label="Sections" flag="--section" htmlFor="pg-grep-sec">
          <input
            id="pg-grep-sec"
            className={`${FIELD} mt-1`}
            value={form.section}
            placeholder="e.g. Methods,Results"
            onChange={(e) => onChange({ ...form, section: e.target.value })}
          />
        </AdvancedField>
        <AdvancedField
          label="Block types"
          flag="--block-type"
          htmlFor="pg-grep-block"
        >
          <input
            id="pg-grep-block"
            className={`${FIELD} mt-1`}
            value={form.blockType}
            placeholder="e.g. table,figure"
            onChange={(e) => onChange({ ...form, blockType: e.target.value })}
          />
        </AdvancedField>
      </div>
    </AdvancedSection>
  </div>
);
